import { useCallback, useState } from "react";
import Media from "./Media";

/** Shape used for a picture until it has loaded and we know its real one. */
const DEFAULT_RATIO = 3 / 2;

/**
 * GALLERY — the grid at the end of a project page
 * ---------------------------------------------------------------------------
 * Pictures are laid out in rows of equal height, each keeping its own shape
 * (so portrait and landscape images sit together without cropping):
 *
 *   ┌──────────────┐ ┌────┐ ┌─────────┐
 *   │              │ │    │ │         │   ← one row, same height
 *   └──────────────┘ └────┘ └─────────┘
 *
 * How: each item's width grows in proportion to its aspect ratio (`--r`),
 * which the browser reports once the picture loads. The CSS does the rest
 * (`.gallery` in styles/project.css).
 *
 * @param {object}   props
 * @param {Array}    props.items   media() objects from projects.js
 * @param {function} [props.onOpen] Called with an item's index when clicked
 *                                  (opens the fullscreen viewer).
 */
export default function Gallery({ items, onOpen }) {
  // Aspect ratio of each item, filled in as pictures load
  const [ratios, setRatios] = useState({});

  const setRatio = useCallback((i, r) => {
    if (!r || !isFinite(r)) return;
    setRatios((prev) => (prev[i] === r ? prev : { ...prev, [i]: r }));
  }, []);

  return (
    <div className="gallery">
      {items.map((item, i) => {
        const ratio = ratios[i] || item.ratio || DEFAULT_RATIO;
        const canOpen = onOpen && item.type !== "vimeo";
        const Tag = canOpen ? "button" : "div";
        return (
          <Tag
            key={i}
            className={`gallery__item ${canOpen ? "zoomable" : ""}`}
            style={{ "--r": ratio }}
            {...(canOpen && {
              type: "button",
              onClick: () => onOpen(i),
              "aria-label": `View ${item.alt || "image"} fullscreen`,
            })}
          >
            <Media item={item} onRatio={(r) => setRatio(i, r)} />
          </Tag>
        );
      })}
    </div>
  );
}
